"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CheckoutClient = void 0;
const commerce_core_1 = require("@broadleaf/commerce-core");
const types_1 = require("./types");
/**
 * Client used for interacting with the checkout related cart APIs.
 */
class CheckoutClient extends commerce_core_1.AbstractClient {
    constructor(options) {
        super({ ...options, basePath: options.basePath || '/api/cart-operations' });
    }
    /**
     * Generate a secure guest checkout token
     *
     * @param cartId the id of the cart to generate a token for
     * @param options the options to the HTTP call
     * @return a guest token response containing the cart and token
     */
    async generateGuestToken(cartId, options) {
        const config = {
            method: 'post',
            url: `/checkout/${cartId}/guest-token`,
            data: {},
            headers: {
                'content-type': 'application/json'
            }
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Generate a secure guest checkout token and associate the guest customer's email address with the cart.
     * Typically this represents the first step in a guest checkout flow.
     *
     * @param cartId the id of the cart
     * @param contactInfoRequest the guest's contact info to add to the cart
     * @param options the options to the HTTP call
     * @return a guest token response containing the cart and guest token
     */
    async checkoutAsGuest(cartId, contactInfoRequest, options) {
        const { cart: { version }, token } = await this.generateGuestToken(cartId, options);
        const cart = await this.updateContactInfoInCart(cartId, contactInfoRequest, { ...options, guestToken: token.tokenString, version });
        return {
            cart,
            token
        };
    }
    /**
     * Validate an existing secure guest checkout token
     *
     * @param cartId the id of the cart to validate
     * @param token the guest checkout token
     * @param options the options to the HTTP call
     * @return a token validation response
     */
    async validateGuestToken(cartId, token, options) {
        const config = {
            method: 'post',
            url: `/checkout/${cartId}/guest-token/validate`,
            data: { guestTokenString: token }
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Update the contact info on the cart
     *
     * @param cartId the id of the cart to update
     * @param contactInfoRequest the contact info to add to the cart
     * @param options the options to the HTTP call
     * @return a single cart
     */
    async updateContactInfoInCart(cartId, contactInfoRequest, options) {
        const config = {
            method: 'post',
            url: `/checkout/${cartId}/contact-info`,
            data: contactInfoRequest
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Update a fullfillment group in the cart
     *
     * @param cartId the id of the cart to update
     * @param referenceNumber the id of the fulfillment group to update
     * @param fulfillmentGroupRequest the fulfillment option information for the group
     * @param options the options to the HTTP call
     * @return a single cart
     */
    async updateFulfillmentGroupInCart(cartId, referenceNumber, fulfillmentGroupRequest, options) {
        const config = {
            method: 'patch',
            url: `/checkout/${cartId}/fulfillment-groups/${referenceNumber}`,
            data: fulfillmentGroupRequest
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieve available fulfillment options for the cart
     *
     * @param cartId the id of the cart
     * @param referenceNumber the id of the fulfillment group
     * @param options the options to the HTTP call
     * @return a list of priced fullfillment options
     */
    async getFulfillmentOptionsForCart(cartId, referenceNumber, options) {
        const config = {
            method: 'get',
            url: `/checkout/${cartId}/fulfillment-options/${referenceNumber}`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieve available priced fulfillment options for the cart
     *
     * @param cartId the id of the cart
     * @param options the options to the HTTP call
     * @return a list of priced fullfillment options
     */
    async getPricedFulfillmentOptionsForCart(cartId, options) {
        const config = {
            method: 'get',
            url: `/checkout/${cartId}/fulfillment-options`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieve available payment method options for the cart
     *
     * @param cartId the id of the cart
     * @param options the options to the HTTP call
     * @return a list of payment method options
     */
    async getPaymentMethodOptionsForCart(cartId, options) {
        const config = {
            method: 'get',
            url: `/checkout/${cartId}/payment-method-options/`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Recalculate the taxes for the cart
     *
     * @param cartId the id of the cart
     * @param options the options to the HTTP call
     * @return a single cart
     */
    async recalculateTaxesForCart(cartId, options) {
        const config = {
            method: 'post',
            url: `/checkout/${cartId}/recalculate-tax`,
            params: {}
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachRecaluateTaxParamsIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Process the checkout
     *
     * @param cartId the id of the cart
     * @param requestId the checkout request id
     * @param options the options to the HTTP call
     * @return a single cart
     */
    async processCheckoutForCart(cartId, requestId, options) {
        return this.processCheckoutRequestForCart(cartId, { requestId }, options);
    }
    /**
     * Process the checkout
     *
     * @param cartId the id of the cart
     * @param requestId the checkout request id
     * @param securityCodes the CVV/CSC codes mapped by payment ID
     * @param options the options to the HTTP call
     * @return a single cart
     */
    async processCheckoutForCartWithSecurityCodes(cartId, requestId, securityCodes, options) {
        return this.processCheckoutRequestForCart(cartId, { requestId, securityCodes }, options);
    }
    /**
     * Process the checkout
     *
     * @param cartId the id of the cart
     * @param request the checkout request
     * @param options the options to the HTTP call
     * @return a single cart
     */
    async processCheckoutRequestForCart(cartId, request, options) {
        const config = {
            method: 'post',
            url: `/checkout/${cartId}/process`,
            data: request || {}
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieve all carts for approval. This is used in B2B scenarios when submitted
     * carts must be approved by someone else in the account before the cart can be
     * fully submitted as an order.
     *
     * @param requestedAccountId the requested account ID that the carts belong to
     * @param customerRef the customer's reference information -- used to extract account ID information
     * @param cq the RSQL filters to be applied to the paginated results
     * @param options the numbered page options to the HTTP call
     * @returns a page of Carts
     */
    async retrieveCartsForApproval(requestedAccountId, customerRef, cq, options) {
        const config = {
            method: 'get',
            url: `/cart-approval`,
            params: { requestedAccountId, customerRef, cq }
        };
        this.attachNumberedPageableParamsIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Request approval for a Cart. This is used in B2B scenarios when an account
     * member needs to submit a cart for approval from someone else in the account
     * before the cart can be fully submitted as an order.
     *
     * @param request The approval request
     * @param cartId The id of the cart needing approval
     * @param options the options to the HTTP call
     * @returns A approval response including the cart and potential error messages.
     */
    async requestCartApproval(request, cartId, options) {
        const config = {
            method: 'post',
            url: `/cart-approval/${cartId}/submit-for-approval`,
            data: request
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Reject approval for a Cart. This is used in B2B scenarios when an account
     * member needs to submit a cart for approval from someone else in the account
     * before the cart can be fully submitted as an order.
     *
     * @param request The rejection request
     * @param cartId The id of the cart needing approval
     * @param options the options to the HTTP call
     * @returns A rejection response including the cart and potential error messages.
     */
    async rejectCartApproval(request, cartId, options) {
        const config = {
            method: 'post',
            url: `/cart-approval/${cartId}/reject-cart`,
            data: request
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Attaches the cart version header to the request `config` if present on `options`.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachCartVersionHeaderIfPresent(config, options) {
        if (options?.version !== undefined) {
            if (!config.headers) {
                config.headers = {};
            }
            config.headers[types_1.CartRequestHeaderType.CART_VERSION] = String(options.version);
        }
    }
    /**
     * Attaches the guest token header to the request `config` if present on `options`.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachGuestTokenHeaderIfPresent(config, options) {
        if (options?.guestToken !== undefined) {
            if (!config.headers) {
                config.headers = {};
            }
            config.headers[types_1.CartRequestHeaderType.GUEST_TOKEN] = options.guestToken;
        }
    }
    /**
     * Attaches the price flag param to the request `config` if present on `options`. This indicates that the cart should be repriced.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachPriceParamIfPresent(config, options) {
        if (options?.price !== undefined) {
            if (!config.params) {
                config.params = {};
            }
            config.params.price = options.price;
        }
    }
    /**
     * Attaches the recalculate tax params to the request `config` if present on `options`. These specify whether to use the real or
     * estimated tax calculations.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachRecaluateTaxParamsIfPresent(config, options) {
        if (options?.actual !== undefined) {
            if (!config.params) {
                config.params = {};
            }
            config.params.actual = options.actual;
        }
        if (options?.estimated !== undefined) {
            if (!config.params) {
                config.params = {};
            }
            config.params.estimated = options.estimated;
        }
    }
    /**
     * Attaches numbered pageable params (page, size, etc.) to the request `config` if present on `options`.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachNumberedPageableParamsIfPresent(config, options) {
        if (options?.page || options?.page === 0) {
            config.params.page = options.page;
        }
        if (options?.size || options?.size === 0) {
            config.params.size = options.size;
        }
        if (options?.sort) {
            config.params.sort = options.sort;
        }
    }
}
exports.CheckoutClient = CheckoutClient;
//# sourceMappingURL=checkout-client.js.map